import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { datadogRum } from '@datadog/browser-rum';
import Card from "../Card/Card";
import DropSelect from "../DropSelect/DropSelect";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import IconButton from "../IconButton/IconButton";
import Table, { ColumnDefinition } from "../Table/Table";
import Tooltip from "../Tooltip/Tooltip";
import { BolderText } from "../BolderText/BolderText";
import Subtitle from "../Subtitle/Subtitle";
import SectionTitle from "../SectionTitle/SectionTitle";
import NoData from "../NoData/NoData";
import { toastService } from "../../services/ToastService";
import SecondaryButton from "../SecondaryButton/SecondaryButton";
import { Icon } from "../Icon/Icon";
import xMarkIcon from "../../assets/images/x-mark.svg";
import deleteIcon from "../../assets/images/bin.svg";
import { Package, Send, FileText, Plus, ExternalLink } from "lucide-react";
import ConfirmationPopup from "../ConfirmationPopup/ConfirmationPopup";
import Pill from "../Pill/Pill";
import { PermissionFlags } from "../PermissionFlags/PermissionFlags";
import { paymentStatusToAbbreviatedTranslationKey, paymentStatusToPillColor, paymentStatusToTranslationKey } from "../../shared/paymentStatusToTranslationKey";
import { saleStatusToPillColor, saleStatusToTranslationKey } from "../../shared/saleStatusToTranslationKey";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { CancelToken } from "../../api/SimpleAPIClient";
import { Modal } from "../Modal/Modal";
import SaleForm from "../SaleForm/SaleForm";
import SalesAPI, { Sale, SaleInstalment, isExternalSale } from "../../api/SalesAPI";
import AddToProductionModal from "../AddToProductionModal/AddToProductionModal";
import ReferralInviteModal from "../ReferralInviteModal/ReferralInviteModal";
import { extractFileName } from "../PrivateResourceLink/PrivateResourceLink";
import FilesAPI from "../../api/FilesAPI";
import useFeatureFlag from "../../hooks/usePermissionFlag";
import { instalmentPaidFieldsChanged } from "../../utils/instalmentPaidFieldsChanged";

// Re-export for backward compatibility
export type { Sale, SaleInstalment };

export type SaleColumn = ColumnDefinition<Sale>;

function SalesSegment() {
  const { t } = useTranslation();
  const { hasAccess: canUpdateOrders } = useFeatureFlag(['update-orders']);
  const { hasAccess: canSeeOrdersAny } = useFeatureFlag(['see-orders', 'see-self-orders']);
  const canSyncProductionPayment = canUpdateOrders && canSeeOrdersAny;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const saleIdParam = searchParams.get('saleId');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sales, setSales] = useState<Sale[]>([]);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    size: 50,
    number: 0
  });

  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
  const [saleForProduction, setSaleForProduction] = useState<Sale | null>(null);
  const [isAddToProductionModalOpen, setIsAddToProductionModalOpen] = useState(false);
  const [isSubmittingToProduction, setIsSubmittingToProduction] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [, setSaleForExternalOrder] = useState<Sale | null>(null);

  const instalmentBaselineRef = useRef<SaleInstalment[] | null>(null);
  const baselineSaleIdRef = useRef<number | undefined>(undefined);
  const [syncProductionModalOpen, setSyncProductionModalOpen] = useState(false);
  const [syncModalSaleId, setSyncModalSaleId] = useState<number | null>(null);
  const [syncInFlight, setSyncInFlight] = useState(false);

  useEffect(() => {
    if (!selectedSale) {
      instalmentBaselineRef.current = null;
      baselineSaleIdRef.current = undefined;
      return;
    }
    if (isCreating || !selectedSale.id) {
      return;
    }
    if (baselineSaleIdRef.current !== selectedSale.id) {
      instalmentBaselineRef.current = JSON.parse(JSON.stringify(selectedSale.instalments || []));
      baselineSaleIdRef.current = selectedSale.id;
    }
  }, [selectedSale, isCreating]);

  useEffect(() => {
    if (!saleIdParam) return;

    SalesAPI.getById(saleIdParam)
      .then((resp) => {
        baselineSaleIdRef.current = undefined;
        setSelectedSale(resp.data);
        setIsCreating(false);
        const next = new URLSearchParams(searchParams);
        next.delete('saleId');
        setSearchParams(next, { replace: true });
      })
      .catch(() => {
        toastService.showToast(
          t("sales.load.error") || "Failed to load sales",
          "error"
        );
      });
  }, [saleIdParam, searchParams, setSearchParams, t]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const setDate = (dateString: string) => {
    if (dateString) {
      const tempDate = new Date(dateString);
      const formatTempDate = format(tempDate, "yyyy-MM-dd");
      setSelectedDate(formatTempDate);
      setCurrentPage(0);
    } else {
      setSelectedDate('');
      setCurrentPage(0);
    }
  };

  const isFiltering = !!selectedStatus || !!selectedPaymentStatus || !!searchQuery || !!selectedDate;

  const loadSales = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await SalesAPI.get({
        status: selectedStatus || undefined,
        paymentStatus: selectedPaymentStatus || undefined,
        search: searchQuery || undefined,
        date: selectedDate || undefined,
        page: currentPage,
        size: pageSize
      });
      
      setSales(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        size: response.data.size,
        number: response.data.number
      });
    } catch (error) {
      toastService.showToast(t("sales.load.error") || "Failed to load sales", 'error');
    } finally {
      setIsLoading(false);
    }
  }, [selectedStatus, selectedPaymentStatus, searchQuery, selectedDate, currentPage, pageSize, t]);


  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const handleAddToProduction = (sale: Sale) => {
    setSaleForProduction(sale);
    setIsAddToProductionModalOpen(true);
  };

  const handleCloseAddToProductionModal = () => {
    setIsAddToProductionModalOpen(false);
    setSaleForProduction(null);
  };

  const handleSubmitToProduction = async (formData: any) => {
    if (!saleForProduction || !saleForProduction.id) {
      toastService.showToast(
        t("toast.errors.missing_data") || "Missing required data",
        "error"
      );
      return;
    }

    setIsSubmittingToProduction(true);
    try {
      await SalesAPI.addToProduction(saleForProduction.id, formData);

      toastService.showToast(
        t("sales.actions.add_to_production.success") || "Order created successfully in production",
        "success"
      );

      handleCloseAddToProductionModal();
      loadSales(); // Reload sales to reflect the orderId update
    } catch (error) {
      console.error('Error creating order from sale:', error);
      toastService.showToast(
        t("sales.actions.add_to_production.error") || "Failed to create order in production",
        "error"
      );
    } finally {
      setIsSubmittingToProduction(false);
    }
  };

  const handleCreateExternalOrder = async (sale: Sale) => {
    // Only allow if b2bType is "OUT"
    if (sale.b2bType !== "OUT") {
      // Show referral modal for non-OUT sales
      setSaleForExternalOrder(sale);
      setIsReferralModalOpen(true);
      return;
    }

    // Create external order for OUT sales using the dedicated endpoint
    try {
      if (!sale.id) {
        toastService.showToast(
          t("sales.actions.create_external_order.error.missing_id") || "Sale ID is required",
          "error"
        );
        return;
      }

      // Call the dedicated endpoint which handles everything
      const response = await SalesAPI.sendToProducent(sale.id);
      
      // The response contains the updated sale with externalOrderId set
      const updatedSale: Sale = response.data;
      
      if (updatedSale.externalOrderId) {
        toastService.showToast(
          t("sales.actions.create_external_order.success") || "External order created successfully",
          "success"
        );
        loadSales(); // Reload to refresh the list
      } else {
        // This shouldn't happen, but log it for debugging
        console.warn('External order ID not found in response:', updatedSale);
        toastService.showToast(
          t("sales.actions.create_external_order.error") || "Failed to create external order",
          "error"
        );
      }
    } catch (error: any) {
      console.error('Error creating external order:', error);
      const errorMessage = error?.response?.data?.message || 
        error?.message || 
        t("sales.actions.create_external_order.error") || 
        "Failed to create external order";
      toastService.showToast(errorMessage, "error");
    }
  };

  const handleCloseReferralModal = () => {
    setIsReferralModalOpen(false);
    setSaleForExternalOrder(null);
  };

  const handleViewContractPDF = async (sale: Sale) => {
    if (!sale?.attachmentUrl) {
      toastService.showToast(
        t("sales.actions.view_contract_pdf.error") || "Contract PDF URL not available",
        "error"
      );
      return;
    }

    try {
      const fileName = extractFileName(sale.attachmentUrl);
      if (!fileName) {
        toastService.showToast(
          t("sales.actions.view_contract_pdf.error") || "Could not extract filename from contract URL",
          "error"
        );
        return;
      }

      const urlResponse = await FilesAPI.getUrl(fileName, 'contracts');
      const signedUrl = urlResponse.data.url;
      
      window.open(signedUrl, "_blank")?.focus();
      
      toastService.showToast(
        t("sales.actions.view_contract_pdf.success") || `Opening contract PDF for sale ${sale.receiptNumber || sale.id}...`,
        "success"
      );
    } catch (error) {
      console.error("Failed to open contract PDF:", error);
      toastService.showToast(
        t("sales.actions.view_contract_pdf.error") || "Failed to open contract PDF",
        "error"
      );
    }
  };

  const search = (input: string, resultsSize: number, setResultsFn: any) => {
    setSearchIsLoading(true);
    SalesAPI.get({ search: input, page: 0, size: resultsSize })
      .then((response) => {
        setSearchIsLoading(false);
        setResultsFn(response.data.content);
      })
      .catch(() => {
        setSearchIsLoading(false);
        setResultsFn([]);
      });
    return CancelToken.source().cancel("Cancelling...");
  };

  const view = (sale: Sale) => {
    baselineSaleIdRef.current = undefined;
    setSelectedSale({ ...sale });
    setIsCreating(false);
  };

  const handleCreateNew = () => {
    baselineSaleIdRef.current = undefined;
    const newSale: Sale = {
      saleDate: new Date().toISOString(),
      clientName: '',
      customerName: '',
      customerSurname: '',
      customerPhone: '',
      customerEmail: '',
      dressName: '',
      dressDescription: '',
      productId: undefined,
      productType: undefined,
      totalPrice: 0,
      currency: 'EUR',
      paymentStatus: 'UNPAID',
      status: 'JUST_SIGNED',
      receiptNumber: '',
    };
    setSelectedSale(newSale);
    setIsCreating(true);
  };

  const closeSaleModal = useCallback(() => {
    setSelectedSale(null);
    setIsCreating(false);
    instalmentBaselineRef.current = null;
    baselineSaleIdRef.current = undefined;
  }, []);

  const closeSyncProductionModal = useCallback(() => {
    setSyncProductionModalOpen(false);
    setSyncModalSaleId(null);
    closeSaleModal();
    loadSales();
  }, [closeSaleModal, loadSales]);

  const buildSaleToSave = (sale: Sale): Sale => ({
    ...sale,
    clientName: sale.clientName || `${sale.customerName || ''} ${sale.customerSurname || ''}`.trim() || '',
    productId: sale.productId,
    productType: sale.productType,
  });

  const handleSaveBeforeGenerate = async (sale: Sale) => {
    if (!sale?.id) return;
    const saleToSave = buildSaleToSave(sale);
    const paidChanged = instalmentPaidFieldsChanged(instalmentBaselineRef.current, saleToSave.instalments);
    const response = await SalesAPI.update(sale.id, saleToSave);
    setSelectedSale(response.data);
    instalmentBaselineRef.current = JSON.parse(JSON.stringify(response.data.instalments || []));
    if (saleToSave.orderId && canSyncProductionPayment && paidChanged) {
      setSyncModalSaleId(response.data.id!);
      closeSaleModal();
      queueMicrotask(() => setSyncProductionModalOpen(true));
    } else {
      setSelectedSale(response.data);
    }
  };

  const handleConfirmSyncProductionPayment = async () => {
    if (syncModalSaleId == null) return;
    setSyncInFlight(true);
    try {
      await SalesAPI.syncInstalmentsToProductionOrder(syncModalSaleId);
      toastService.showToast(
        t("sales.sync_production_payment.success") || "Production order payment plan updated",
        "success"
      );
    } catch {
      toastService.showToast(
        t("sales.sync_production_payment.error") || "Failed to update production order payments",
        "error"
      );
    } finally {
      setSyncInFlight(false);
      closeSyncProductionModal();
    }
  };

  const handleSaveSale = async () => {
    if (!selectedSale) return;
    
    try {
      const saleToSave = buildSaleToSave(selectedSale);

      if (isCreating) {
        console.log('Creating sale with productId:', saleToSave.productId, 'productType:', saleToSave.productType);
        const response = await SalesAPI.add(saleToSave);
        console.log('Created sale response:', response.data);
        
        datadogRum.addAction('Create Sale', {
          productName: saleToSave.dressName || '',
          totalPrice: saleToSave.totalPrice || 0,
          currency: saleToSave.currency || '',
          source: 'sales_page'
        });
        
        toastService.showToast(t("sales.create.success") || "Sale created successfully", "success");
      } else {
        if (!selectedSale.id) {
          toastService.showToast(t("sales.update.error") || "Sale ID is required", 'error');
          return;
        }
        const paidChanged = instalmentPaidFieldsChanged(instalmentBaselineRef.current, saleToSave.instalments);
        const response = await SalesAPI.update(selectedSale.id, saleToSave);

        datadogRum.addAction('Update Sale', {
          productName: saleToSave.dressName || '',
          totalPrice: saleToSave.totalPrice || 0,
          currency: saleToSave.currency || '',
          source: 'sales_page'
        });

        toastService.showToast(t("sales.update.success") || "Sale updated successfully", "success");
        instalmentBaselineRef.current = JSON.parse(JSON.stringify(response.data.instalments || []));

        if (saleToSave.orderId && canSyncProductionPayment && paidChanged) {
          setSyncModalSaleId(response.data.id!);
          closeSaleModal();
          queueMicrotask(() => setSyncProductionModalOpen(true));
        } else {
          closeSaleModal();
          loadSales();
        }
        return;
      }
      closeSaleModal();
      loadSales();
    } catch (error) {
      toastService.showToast(t("sales.save.error") || "Failed to save sale", 'error');
    }
  };

  const handleDeleteSale = async (sale: Sale) => {
    if (!sale.id) {
      toastService.showToast(t("sales.delete.error") || "Sale ID is required", 'error');
      return;
    }

    try {
      await SalesAPI.remove(sale.id);
      toastService.showToast(t("sales.delete.success") || "Sale deleted successfully", "success");
      loadSales();
    } catch (error) {
      toastService.showToast(t("sales.delete.error") || "Failed to delete sale", 'error');
    }
  };

  const saleColumns: SaleColumn[] = [
    {
      key: 'saleDate',
      title: t("sales.details.date") || "Sale Date",
      render: (sale) => <BolderText text={sale.saleDate || ''} />,
    },
    {
      key: 'clientName',
      title: t("sales.details.client") || "Client",
      render: (sale) => (
        <Tooltip content={sale.clientName}>
          <p className="w-36 truncate">{sale.clientName}</p>
        </Tooltip>
      ),
    },
    {
      key: 'receiptNumber',
      title: t("sales.details.receipt_number") || "Receipt Number",
      render: (sale) => (
        <Tooltip content={sale.receiptNumber}>
          <p className="w-32 truncate font-medium">{sale.receiptNumber}</p>
        </Tooltip>
      ),
    },
    {
      key: 'dressName',
      title: t("sales.details.dress") || "Dress",
      render: (sale) => (
        sale.dressDescription ? (
          <Tooltip content={sale.dressDescription}>
            <p className="w-32 truncate">{sale.dressName}</p>
          </Tooltip>
        ) : (
          <p className="w-32 truncate">{sale.dressName}</p>
        )
      ),
    },
    {
      key: 'totalPrice',
      title: t("sales.details.price") || "Price",
      render: (sale) => (
        <span className="font-medium">
          {sale.totalPrice.toLocaleString()} {sale.currency}
        </span>
      ),
    },
    {
      key: 'paymentStatus',
      title: t("sales.details.payment_status") || "Payment Status",
      render: (sale) => (
        <Tooltip content={t(paymentStatusToTranslationKey(sale.paymentStatus as any))}>
          <Pill 
            text={t(paymentStatusToAbbreviatedTranslationKey(sale.paymentStatus as any))} 
            color={paymentStatusToPillColor(sale.paymentStatus as any)}
          />
        </Tooltip>
      ),
    },
    {
      key: 'status',
      title: t("sales.details.status") || "Status",
      render: (sale) => {
        const translationKey = saleStatusToTranslationKey(sale.status);
        const label = translationKey === "global.unknown" ? (sale.status || "-") : t(translationKey);
        return (
          <Pill 
            text={label}
            color={saleStatusToPillColor(sale.status)}
          />
        );
      },
    },
  ];

  const renderActions = (sale: Sale): React.ReactNode => {
    return (
      <span className="flex gap-2">
        {sale.attachmentUrl && (
          <IconButton
            actionName="View Contract PDF"
            onClick={() => {
              handleViewContractPDF(sale);
            }}
          >
            <Tooltip content={t("sales.actions.view_contract_pdf") || "View Contract PDF"}>
              <FileText className="w-5 h-5 text-purple-600" />
            </Tooltip>
          </IconButton>
        )}
        {sale.orderId ? (
          <IconButton
            actionName="View Order"
            onClick={() => {
              navigate(`/production-orders?orderId=${sale.orderId}`);
            }}
          >
            <Tooltip content={t("sales.actions.view_order") || "View Order"}>
              <ExternalLink className="w-5 h-5 text-blue-600" />
            </Tooltip>
          </IconButton>
        ) : sale.externalOrderId ? (
          <IconButton
            actionName="View External Order"
            onClick={() => {
              navigate(`/orders?partnersOrders=true&orderId=${sale.externalOrderId}`);
            }}
          >
            <Tooltip content={t("sales.actions.view_external_order") || "View External Order"}>
              <ExternalLink className="w-5 h-5 text-green-600" />
            </Tooltip>
          </IconButton>
        ) : (
          // Only show "Add to Production" if sale is NOT external (b2bType !== "OUT")
          !isExternalSale(sale) && (
            <PermissionFlags requiredFlags={['order-add']}>
              <IconButton
                actionName="Add to Production"
                onClick={() => {
                  handleAddToProduction(sale);
                }}
              >
                <Tooltip content={t("sales.actions.add_to_production") || "Add to Production"}>
                  <Package className="w-5 h-5 text-blue-600" />
                </Tooltip>
              </IconButton>
            </PermissionFlags>
          )
        )}
        {/* Only show "Request to producent" if external order hasn't been created yet */}
        {!sale.externalOrderId && (
          <PermissionFlags requiredFlags={['order-add']}>
            <IconButton
              actionName="Request to producent"
              onClick={() => {
                handleCreateExternalOrder(sale);
              }}
            >
              <Tooltip content={
                sale.b2bType === "OUT" 
                  ? (t("sales.actions.create_external_order") || "Request to producent")
                  : (t("sales.actions.create_external_order.info") || "Click to learn about inviting producents to eAteliers")
              }>
                <Send className="w-5 h-5 text-green-600" />
              </Tooltip>
            </IconButton>
          </PermissionFlags>
        )}
        <IconButton
          actionName="Delete Sale"
        >
          <ConfirmationPopup 
            onConfirm={() => handleDeleteSale(sale)} 
            confirmText={"global.confirm"} 
            title={`${t("global.remove")}: ${sale.receiptNumber}`}
          >
            <Icon url={deleteIcon} cssStyles="w-5"/>
          </ConfirmationPopup>
        </IconButton>
      </span>
    );
  };

  const isDifferentMonth = (date1: Date, date2: Date) => {
    return date1.getMonth() !== date2.getMonth() || date1.getFullYear() !== date2.getFullYear();
  };

  return (
    <div className="mt-5 space-y-5">
      <Card>
        <>
          <div className="flex-col">
            <div className="md:flex md:flex-row md:items-center md:space-x-3">
              <div className="flex items-center space-x-3 mb-2 md:mb-0 w-full">
                <DropSelect compact onSelect={(e: any) => setDate(e.target.value)} value={selectedDate}/>
                <div className="flex-1">
                  <Autocomplete
                    resultsSize={15}
                    searchFn={search}
                    onSelect={() => null}
                    placeholder={"sales.search.placeholder"} 
                    isLoading={searchIsLoading}
                    errorMessage={"global.empty_results"}
                    toElementFn={(sale: Sale) => (
                      <div 
                        key={sale.id}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                        onClick={() => view(sale)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{sale.clientName}</p>
                            <p className="text-sm text-gray-600">{sale.dressName}</p>
                            <p className="text-xs text-gray-500">{sale.receiptNumber}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{sale.totalPrice.toLocaleString()} {sale.currency}</p>
                            <p className="text-xs text-gray-500">{sale.saleDate || ''}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3">
            <PrimaryButton
              text={
                <span className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  {t("sales.register_new_sale") || "Register New Sale"}
                </span>
              }
              fn={handleCreateNew}
              actionName="Register New Sale"
            />
            <PermissionFlags requiredFlags={['edit-contract-template']}>
              <PrimaryButton
                text={
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {t("sales.configure_contract") || "Configure Contract Template"}
                  </span>
                }
                fn={() => navigate('/contracts')}
                actionName="Configure Contract Template"
              />
            </PermissionFlags>
            </div>
          </div>
          {isFiltering && (
            <div className="mt-3">
              <SecondaryButton 
                actionName="Clear Filters" 
                text={t("filters.reset")!} 
                fn={() => {
                  setSelectedStatus('');
                  setSelectedPaymentStatus('');
                  setSearchQuery('');
                  setSelectedDate('');
                  setCurrentPage(0);
                }}
                icon={
                  <Icon url={xMarkIcon} cssStyles="w-4 h-4" />
                }
                compact
              />
            </div>
          )}
        </>
      </Card>

      <Card>
        <>
          <div className="container">
            <Table<Sale>
              isLoading={isLoading}
              data={sales}
              columns={saleColumns}
              onRowClick={(sale) => {
                view(sale);
              }}
              compareFunction={(item, selectedItem) => item.id === selectedItem.id}
              emptyListComponent={<NoData message={t("sales.empty") || "No sales found"} />}
              selectedItems={[]}
              isSelectable={false}
              dividerCondition={(current, next) => 
                isDifferentMonth(current.saleDate ? new Date(current.saleDate + 'T00:00:00') : new Date(), next.saleDate ? new Date(next.saleDate + 'T00:00:00') : new Date())
              }
              renderDivider={(current) => (
                <div className="mt-10 mb-4 flex items-end space-x-2">
                  <Subtitle text={`${t(`global.${format(current.saleDate ? new Date(current.saleDate + 'T00:00:00') : new Date(), "MMMM").toLowerCase()}`)}`} />
                  <SectionTitle text={`${format(current.saleDate ? new Date(current.saleDate + 'T00:00:00') : new Date(), "yyyy")}`} />
                </div>
              )}
              renderActions={renderActions}
            />
          </div>
          {/* Pagination controls */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {t("global.pagination.show", "Show")}:
              </span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-gray-600">
                {t("global.pagination.per_page", "per page")}
              </span>
            </div>

            {/* Pagination buttons */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-2">
                <SecondaryButton
                  text={t("global.previous", "Prev") as string}
                  fn={() => setCurrentPage(Math.max(0, pagination.number - 1))}
                  isDisabled={pagination.number === 0}
                  actionName="Pagination Prev"
                />
                <span className="px-3 py-1 mx-1 text-sm text-gray-600">
                  {t("global.pagination.page", "Page {{page}} of {{totalPages}}", {
                    page: pagination.number + 1,
                    totalPages: pagination.totalPages
                  })}
                </span>
                <SecondaryButton
                  text={t("global.next", "Next") as string}
                  fn={() => setCurrentPage(Math.min(pagination.totalPages - 1, pagination.number + 1))}
                  isDisabled={pagination.number >= pagination.totalPages - 1}
                  actionName="Pagination Next"
                />
              </div>
            )}
          </div>
        </>
      </Card>

      {selectedSale && (
        <Modal 
          modalTitle={isCreating ? t("sales.form.create_title") || "Create Sale" : t("sales.form.title") || "Sale Details"}
          canClose 
          onClose={closeSaleModal}
        >
          <>
            <SaleForm 
              sale={selectedSale} 
              onSaleChange={setSelectedSale}
              isDisabled={false}
              onValidationChange={setIsFormValid}
              onSaveBeforeGenerate={handleSaveBeforeGenerate}
            />
            <div className="flex justify-center mt-5">
              <PrimaryButton 
                text={isCreating ? t("global.create") || "Create" : t("global.update") || "Update"} 
                fn={handleSaveSale}
                actionName={isCreating ? "Create Sale" : "Update Sale"}
                isDisabled={!isFormValid}
              />
            </div>
          </>
        </Modal>
      )}
      {isAddToProductionModalOpen && saleForProduction && (
        <AddToProductionModal
          sale={saleForProduction}
          isOpen={isAddToProductionModalOpen}
          onClose={handleCloseAddToProductionModal}
          onSubmit={handleSubmitToProduction}
          isLoading={isSubmittingToProduction}
        />
      )}
      <ReferralInviteModal
        isOpen={isReferralModalOpen}
        onClose={handleCloseReferralModal}
      />

      {syncProductionModalOpen && (
        <Modal
          modalTitle="sales.sync_production_payment.title"
          canClose={!syncInFlight}
          onClose={closeSyncProductionModal}
        >
          <div className="space-y-5 p-5">
            <p className="text-slate-600">
              {t("sales.sync_production_payment.description") ||
                "You updated the payment status for this sale. There is an associated order in production. Do you want to update the payment instalments there as well?"}
            </p>
            <div className="flex flex-wrap justify-end gap-3 pt-2 border-t border-slate-200">
              <SecondaryButton
                text={t("sales.sync_production_payment.dismiss") || "Not now"}
                fn={closeSyncProductionModal}
                actionName="Dismiss sync production payment"
                isDisabled={syncInFlight}
              />
              <PrimaryButton
                text={t("sales.sync_production_payment.confirm") || "Update production order"}
                fn={handleConfirmSyncProductionPayment}
                actionName="Confirm sync production payment"
                isDisabled={syncInFlight}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SalesSegment;

